import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {EventEmitter, inject, Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {SecurityTokenDto} from '@core/auth/dtos/securityTokenDto';
import {
	BehaviorSubject,
	EMPTY,
	Observable,
	ReplaySubject,
	Subject,
	catchError,
	debounceTime,
	distinctUntilChanged,
	map,
	merge,
	of,
	share,
	shareReplay,
	switchMap,
	tap,
	timer,
	take
} from 'rxjs';
import {Result, ResultWithValue} from '../../../apiResults/result';
import {CLAIMS} from '../../../permissions/constants/claims-names';
import {ALL_SUPPLIER_ROLES, ROLES} from '../../../permissions/constants/roles';
import {ServiceResponse} from '../../../service-response/serviceResponse';
import {UserProfileService} from '../../../user-profile/services/user-profile.service';
import {TokenPair} from '../../dtos/TokenPair';
import {LoginDto} from '../../dtos/loginDto';
import {LoginResultDto} from '../../dtos/loginResultDto';
import {MfaLoginDto} from '../../dtos/mfaLoginDto';
import {MfaSetupResultDto} from '../../dtos/mfaSetupResultDto';
import {ResetPasswordDto} from '../../dtos/resetPasswordDto';
import {ResetPasswordResultDto} from '../../dtos/resetPasswordResultDto';
import {ssoConfigsDto} from '../../dtos/ssoConfigsDto';
import {JwtService} from '../jwt/jwt.service';
import {TermsOfUseService} from './../../../terms-of-use/services/terms-of-use.service';
import {CacheService} from '@core/services/cache/cache.service';

export const AUTH_API = {
	Login: "api/auth",
	LoginSSO: "api/auth",
	CheckSSO: "api/auth/checksso",
	Reset: "api/auth/reset",
	IsTokenValid: "api/auth/isTokenValid",
	Logout: (token: string) => `api/auth/logout?token=${token}`,
	Recover: (email: string) => `api/auth/reset/${email}`,
	Refresh: (token: string) => `api/auth/refresh?token=${token}`,
	Accept: (token: string) => `api/auth/accept?token=${token}`,
	GetMfaSetup: "api/auth/mfasetup",
	EnableMfa: (passcode: string) => `api/auth/mfasetup?passcode=${passcode}`,
	DisableMfa: "api/auth/mfasetup",
	LoginMfa: "api/auth/loginmfa",
} as const;

@Injectable({
	providedIn: 'root'
})
export class RefreshQueueService {
	public refreshQueue = new Subject<void>();

	public pushRefresh() {
		this.refreshQueue.next();
	}

	public logoutQueue = new Subject<void>();
	public refreshToken$ = new Subject<ResultWithValue<LoginResultDto>>();
}


@Injectable({
	providedIn: 'root'
})
export class AuthService {

	private logoutQueue$ = this.refreshQueueService.logoutQueue.pipe(
		map(() => this.logout(false))
	);
	private refreshQueue$ = this.refreshQueueService.refreshQueue.pipe(
		debounceTime(500),
		switchMap(() => this.refreshToken()),
		share()
	);

	public userLoggedIn = new EventEmitter();
	cacheService = inject(CacheService);
	token$ = new BehaviorSubject<TokenPair | undefined>(this.jwtService.getToken());
	isAuthenticated$: Observable<boolean> = this.token$.pipe(
		tap(tokenPair => this.jwtService.saveToken(tokenPair)),
		switchMap(token => {
			if (!token)
				return of(false);

			if (!this.jwtService.isTokenExpired(token.accessToken)) {
				return of(true);
			}

			return this.refreshToken().pipe(
				map(result => !result.isFailure)
			);
		}),
		shareReplay(),
		distinctUntilChanged()
	)

	constructor(private http: HttpClient,
				private jwtService: JwtService,
				private router: Router,
				private termsOfUseService: TermsOfUseService,
				private userProfileService: UserProfileService,
				private refreshQueueService: RefreshQueueService) {
		merge(this.isAuthenticated$, this.token$).subscribe();
		this.refreshQueue$.subscribe();
		this.logoutQueue$.subscribe();
	}

	public checksso() {
		return this.http.get<ResultWithValue<ssoConfigsDto>>(AUTH_API.CheckSSO).pipe(
			catchError((err) => {
				const fail: ResultWithValue<ssoConfigsDto> = {
					isFailure: true,
					isSuccess: false,
					status: err.status,
					error: err.error?.error,
					value: {} as ssoConfigsDto
				}
				return of(fail);
			})
		);
	}

	public logIn(dto: LoginDto) {
		return this.http.post<ResultWithValue<LoginResultDto>>(AUTH_API.Login, dto).pipe(
			catchError(err => {
				const fail: ResultWithValue<LoginResultDto> = {
					isFailure: true,
					isSuccess: false,
					status: err.status,
					error: err.error?.error,
					value: {isAuthSuccessful: false, isMfaEnabled: false}
				}
				return of(fail);
			}),
			tap(result => this.handleLoginResult(result))
		);
	}

	public loginMfa(mfaLoginDto: MfaLoginDto) {
		return this.http.post<ResultWithValue<LoginResultDto>>(AUTH_API.LoginMfa, mfaLoginDto).pipe(
			catchError((err) => {
				const fail: ResultWithValue<LoginResultDto> = {
					isFailure: true,
					isSuccess: false,
					status: err.status,
					error: err.error?.error,
					value: {isAuthSuccessful: false, isMfaEnabled: true}
				}
				return of(fail);
			}),
			tap(result => this.handleLoginResult(result))
		);
	}

	private handleLoginResult(loginResult: ResultWithValue<LoginResultDto>) {
		if (loginResult.isSuccess && loginResult.value.isAuthSuccessful) {
			if (loginResult.value.tokenPair) {
				this.token$.next(loginResult.value.tokenPair);
				this.termsOfUseService.refreshTermsOfUse();
				this.userProfileService.refreshUserProfile();
				this.userLoggedIn.emit();
			}
		}
	}


	lastRefreshedToken?: string;


	public refreshToken() {
		const token = this.jwtService.getToken();
		if (!token)
			return EMPTY;

		// Check if we already have a valid access token
		const currentAuthResult = this.getCurrentAuthResult();
		if (currentAuthResult?.isSuccess) {
			return of(currentAuthResult);
		}

		// Prevent multiple refresh attempts with the same token
		if (this.lastRefreshedToken === token.refreshToken) {
			return this.refreshQueueService.refreshToken$.pipe(
				take(1),
				catchError(() => EMPTY)
			);
		}

		this.lastRefreshedToken = token.refreshToken;

		return this.http.post<ResultWithValue<LoginResultDto>>(AUTH_API.Refresh(token.refreshToken), {}).pipe(
			catchError((err: HttpErrorResponse) => {
				// Clear the last refreshed token on error
				this.lastRefreshedToken = undefined;

				// Check if we have a valid token before logging out
				const currentAuth = this.getCurrentAuthResult();
				if (currentAuth?.isSuccess) {
					return of(currentAuth);
				}

				// If refresh fails and no valid token, logout after retry
				return timer(1000).pipe(
					map(() => {
						const finalCheck = this.getCurrentAuthResult();
						if (finalCheck?.isSuccess) {
							return finalCheck;
						}
						this.logout();
						return {
							isFailure: true,
							isSuccess: false,
							status: err.status,
							error: err.error?.error,
							value: {} as LoginResultDto
						} as ResultWithValue<LoginResultDto>;
					})
				);
			}),
			tap(result => {
				if (result.isSuccess && result.value.tokenPair) {
					this.token$.next(result.value.tokenPair);
					this.refreshQueueService.refreshToken$.next(result);
				}
			})
		);
	}

	private getCurrentAuthResult() {
		const newToken = this.jwtService.getToken();
		if (newToken && !this.jwtService.isTokenExpired(newToken.accessToken)) {
			const successPreviously: ResultWithValue<LoginResultDto> = {
				isFailure: false,
				isSuccess: true,
				status: 200,
				value: {
					tokenPair: newToken
				} as LoginResultDto
			}
			return successPreviously;
		}
		return null;
	}

	public isAuthenticated(): boolean {
		const tokenPair = this.jwtService.getToken();
		if (tokenPair)
			return !this.jwtService.isTokenExpired(tokenPair.accessToken);

		return false;
	}

	public canReauthenticated(): boolean {
		const tokenPair = this.jwtService.getToken();
		if (tokenPair)
			return !this.jwtService.isTokenExpired(tokenPair.refreshToken);

		return false;
	}

	public hasClaim(claimKey: string, claimValues: string) {
		const tokenPair = this.jwtService.getToken();
		if (!tokenPair)
			return false;

		const decoded = this.jwtService.decodeToken(tokenPair.accessToken);

		const claim = decoded[claimKey];
		const type = typeof (claim);

		if (type === "object")
			return (claim as any[]).includes(claimValues);

		if (type === "string")
			return claim === claimValues

		return false;
	}

	public isTemporaryAccess() {
		return this.hasClaim(CLAIMS.TemporaryRoles, ROLES.Admin);
	}

	public getRemainingTemporaryAccessTime() {
		const tokenPair = this.jwtService.getToken();
		if (!tokenPair)
			return 0;


		const decoded = this.jwtService.decodeToken(tokenPair.accessToken);

		const claim = decoded['Hr-TemporaryExpirationDate'];

		return claim;
	}

	public hasRole(roles: string[]) {
		return roles.some(role => this.hasClaim(CLAIMS.Role, role));
	}

	public isAdmin() {
		return this.hasClaim(CLAIMS.Role, ROLES.Admin);
	}

	public isMobility() {
		return this.hasClaim(CLAIMS.Role, ROLES.Mobility);
	}

	public isCollaborator() {
		const temporaryAdmin = this.isTemporaryAccess();
		if (temporaryAdmin)
			return false;
		return this.hasClaim(CLAIMS.Role, ROLES.Collaborator);
	}

	public isSupplier() {
		const temporaryAdmin = this.isTemporaryAccess();
		if (temporaryAdmin)
			return false;
		return this.hasClaim(CLAIMS.Role, ROLES.Supplier);
	}

	public isImmigration() {
		const temporaryAdmin = this.isTemporaryAccess();
		if (temporaryAdmin)
			return false;
		return this.hasClaim(CLAIMS.Role, ROLES.Immigration);
	}

	public isTax() {
		const temporaryAdmin = this.isTemporaryAccess();
		if (temporaryAdmin)
			return false;
		return this.hasClaim(CLAIMS.Role, ROLES.Tax);
	}

	public isGenericSupplier() {
		const temporaryAdmin = this.isTemporaryAccess();
		if (temporaryAdmin)
			return false;
		return this.hasRole(ALL_SUPPLIER_ROLES);
	}

	public isStakeholder() {
		const temporaryAdmin = this.isTemporaryAccess();
		if (temporaryAdmin)
			return false;
		return this.hasClaim(CLAIMS.Role, ROLES.Stakeholder);
	}

    public isGenericStakeholder() {
        const temporaryAdmin = this.isTemporaryAccess();
        if (temporaryAdmin)
            return false;
        return this.hasClaim(CLAIMS.Role, ROLES.Stakeholder);
    }

	public recoverPassword(email: string) {
		return this.http.get<ResultWithValue<ResetPasswordResultDto>>(AUTH_API.Recover(email));
	}

	/**
	 * This method is not to be confused the ChangePassword() on the UserProfile screen.
	 * This is specifically for 'forgot password', and 'new user' (aka, not signed in).
	 */
	public resetPassword(dto: ResetPasswordDto) {
		return this.http.post<ResultWithValue<LoginResultDto>>(AUTH_API.Reset, dto).pipe(
			catchError((err: HttpErrorResponse) => {
				const fail: ResultWithValue<LoginResultDto> = {
					isFailure: true,
					isSuccess: false,
					status: err.status,
					error: err.error?.error,
					value: {} as LoginResultDto
				}
				return of(fail);
			}),
			tap(loginResult => this.handleResetPassword(loginResult))
		);
	}

	public isTokenValid(securityTokenDto: SecurityTokenDto) {
		return this.http.post<ResultWithValue<boolean>>(AUTH_API.IsTokenValid, securityTokenDto).pipe(
			catchError((err) => {
				const fail: ResultWithValue<boolean> = {
					isFailure: true,
					isSuccess: false,
					status: err.status,
					error: err.error?.error,
					value: true
				}
				return of(fail);
			})
		);
	}

	private handleResetPassword(loginResult: ResultWithValue<LoginResultDto>) {
		if (loginResult.status == 200) {
			if (loginResult.value.tokenPair) {
				this.jwtService.saveToken(loginResult.value.tokenPair);
			}
		}
	}

	public logout(logoutInApi = true) {
		this.cacheService.invalidateAll();
		this.termsOfUseService.refreshTermsOfUse();
		const token = this.jwtService.getToken();
		if (token && token.refreshToken && logoutInApi) {
			this.http.post(AUTH_API.Logout(token.refreshToken), {}).pipe(shareReplay()).subscribe();
		}
		this.jwtService.clearToken();
        localStorage.clear();
		this.router.navigate(['/']);
		this.token$.next(undefined);
	}

	public getMfaSetup() {
		const service$ = this.http.get<ResultWithValue<MfaSetupResultDto>>(AUTH_API.GetMfaSetup).pipe(
			shareReplay(),
			catchError((err) => of<ResultWithValue<MfaSetupResultDto>>(err)),
		);

		return new ServiceResponse(service$);
	}

	public enableMfa(passcode: string) {
		return this.http.post<Result>(AUTH_API.EnableMfa(passcode), {}).pipe(
			catchError((err: HttpErrorResponse) => this.catchErrorResult(err))
		);
	}

	public disableMfa() {
		return this.http.delete<Result>(AUTH_API.DisableMfa, {}).pipe(
			catchError((err: HttpErrorResponse) => this.catchErrorResult(err))
		);
	}

	private catchErrorResult(err: HttpErrorResponse) {
		const fail: Result = {
			isFailure: true,
			isSuccess: false,
			status: err.status,
		}
		return of(fail);
	}
}
